import { motion } from "framer-motion";
import { MapPin, PenLine, FileCheck, HardHat } from "lucide-react";

const steps = [
  {
    icon: MapPin,
    title: "Recherche du terrain",
    text: "Zone calme, sans vis-à-vis, plat de préférence : un lot bien choisi, c'est des milliers d'euros économisés en raccordements et en terrassement.",
  },
  {
    icon: PenLine,
    title: "Compromis et financement",
    text: "Signature du compromis, puis verrouillez votre plan de financement : apport, prêt, PTZ éventuel. C'est le moment de faire jouer la concurrence.",
  },
  {
    icon: FileCheck,
    title: "Dépôt du permis de construire",
    text: "Plans, insertion paysagère, pièces graphiques : un dossier complet évite les refus et les allers-retours avec la mairie.",
  },
  {
    icon: HardHat,
    title: "Construction et réception",
    text: "Fondations, gros œuvre, second œuvre, puis la réception des travaux : chaque étape clé se vérifie, idéalement avec un contrôle indépendant.",
  },
];

const CONSTRUCTION_IMG =
  "https://images.unsplash.com/photo-1672627170267-fca17bb54156?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxob3VzZSUyMGNvbnN0cnVjdGlvbiUyMHNpdGV8ZW58MHx8fHwxNzg3ODQyODg5fDA&ixlib=rb-4.1.0&q=85";

export default function Timeline() {
  return (
    <section id="etapes" className="bg-sand/50 py-24 lg:py-32" data-testid="timeline-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="lg:sticky lg:top-28"
            >
              <p className="font-sub text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-terra mb-4">Le parcours</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mb-6">
                Les étapes <em className="italic text-terra">clés</em>
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-ink/70 mb-10">
                Construire, c'est une succession d'étapes dans le bon ordre. Voici la feuille de route que nous détaillons dans nos guides et nos vidéos.
              </p>
              <div className="rounded-xl overflow-hidden border border-border shadow-lift">
                <img src={CONSTRUCTION_IMG} alt="Chantier de construction d'une maison" className="w-full aspect-[4/3] object-cover" />
              </div>
            </motion.div>
          </div>
          <div className="lg:col-span-7">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="flex gap-6 border-t border-ink/10 py-10 first:border-t-0 first:pt-0"
                data-testid={`timeline-step-${i + 1}`}
              >
                <span className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-lg bg-white border border-border text-terra shadow-soft">
                  <s.icon className="w-5 h-5" />
                </span>
                <div>
                  <p className="font-sub text-xs uppercase tracking-[0.2em] text-stone2 mb-2">Étape {i + 1}</p>
                  <h3 className="font-display text-2xl sm:text-3xl leading-snug mb-3">{s.title}</h3>
                  <p className="text-sm sm:text-base leading-relaxed text-ink/70 max-w-lg">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
